/**
 * LandingHowItWorks Component
 *
 * "How it Works" section with 4 alternating image/text cards.
 * Features scroll-triggered animations with direction based on layout.
 * "Build with Lev" scrolls to the top unless the caller passes `onBuildWithLev`.
 */
"use client";

import Image from "next/image";
import { AnimatedSection } from "@/components/atoms/AnimatedSection";
import { ArrowUpRight } from "lucide-react";
import { trackLandingCtaClicked } from "@/lib/analytics/client";

interface HowItWorksCard {
  tag: string;
  title: string;
  description: string;
  imageUrl: string;
  imagePosition: "left" | "right";
}

const cards: HowItWorksCard[] = [
  {
    tag: "IDEATION",
    title: "The space to brainstorm",
    description:
      "Lev operates on an infinite canvas, giving founders a space to explore and refine ideas with the same structure and creativity as a real team session.",
    imageUrl: "/landing/hiw-box-left.png",
    imagePosition: "left",
  },
  {
    tag: "VALIDATION",
    title: "Guided by a proven process",
    description:
      "Built on our step-by-step company-building process, Lev helps you go from idea to company.",
    imageUrl: "/landing/hiw-box-right.png",
    imagePosition: "right",
  },
  {
    tag: "ITERATION",
    title: "A Helping Voice",
    description:
      "Lev identifies key areas for improvement and offers practical guidance to keep building momentum.",
    imageUrl: "/landing/hiw-bottom-left.png",
    imagePosition: "left",
  },
  {
    tag: "EXECUTION",
    title: "From Ideas to Action",
    description:
      "From go-to-market plans and prospect lists to landing pages and product specs, Lev generates the foundational assets needed to get your business off the ground.",
    imageUrl: "/landing/hiw-top-right.png",
    imagePosition: "right",
  },
];

function HowItWorksCard({
  card,
  index,
  onLearnMore,
}: {
  card: HowItWorksCard;
  index: number;
  onLearnMore: () => void;
}) {
  const isImageLeft = card.imagePosition === "left";

  const imageElement = (
    <AnimatedSection
      direction={isImageLeft ? "left" : "right"}
      delay={index * 0.1}
      className="flex-1 w-full md:w-1/2"
    >
      <div className="relative w-full aspect-[4/3] rounded-2xl overflow-hidden bg-landing-bg-medium">
        <Image
          src={card.imageUrl}
          alt={card.title}
          fill
          className="object-cover"
          sizes="(max-width: 768px) 100vw, 50vw"
        />
      </div>
    </AnimatedSection>
  );

  const contentElement = (
    <AnimatedSection
      direction={isImageLeft ? "right" : "left"}
      delay={index * 0.1 + 0.1}
      className="flex-1 w-full md:w-1/2 flex flex-col justify-center"
    >
      <div className="bg-landing-bg-medium inline-block px-2.5 py-2 rounded-md mb-4 w-fit">
        <span className="font-mono text-xs text-landing-secondary uppercase">{card.tag}</span>
      </div>
      <h3 className="text-2xl md:text-[32px] text-landing-dark tracking-tight mb-4">
        {card.title}
      </h3>
      <p className="text-base md:text-lg text-landing-secondary leading-relaxed mb-6">
        {card.description}
      </p>
      <button
        type="button"
        onClick={() => {
          trackLandingCtaClicked({ section: "how-it-works", cardIndex: index });
          onLearnMore();
        }}
        className="group inline-flex items-center gap-2 bg-landing-dark text-white px-6 py-3 rounded-full text-sm font-medium hover:bg-[#3D4149] transition-colors w-fit cursor-pointer"
      >
        Build with Lev
      </button>
    </AnimatedSection>
  );

  return (
    <div className="flex flex-col md:flex-row gap-8 md:gap-12">
      {isImageLeft ? (
        <>
          {imageElement}
          {contentElement}
        </>
      ) : (
        <>
          {contentElement}
          {imageElement}
        </>
      )}
    </div>
  );
}

export interface LandingHowItWorksProps {
  /** Replaces the default "Build with Lev" action (scroll to the top of the page). */
  onBuildWithLev?: () => void;
  title?: string;
  subtitle?: string;
}

export function LandingHowItWorks({
  onBuildWithLev,
  title = "How it Works",
  subtitle = "Founders like you are already building with Lev.",
}: LandingHowItWorksProps = {}) {
  const handleLearnMore =
    onBuildWithLev ?? (() => window.scrollTo({ top: 0, behavior: "smooth" }));

  return (
    // overflow-x-clip: each card slides in sideways (AnimatedSection), and
    // before it reveals, its offset widens the page on a phone.
    <section id="how-it-works" className="overflow-x-clip bg-landing-bg-light px-4 pb-20 pt-[90px]">
      <div className="w-full max-w-[1032px] mx-auto">
        <AnimatedSection>
          <h2 className="text-[28px] md:text-[36px] text-landing-dark tracking-tight mb-4">
            {title}
          </h2>
          <p className="text-base md:text-lg text-landing-secondary mb-12">{subtitle}</p>
        </AnimatedSection>

        <div className="flex flex-col gap-16 md:gap-20">
          {cards.map((card, index) => (
            <HowItWorksCard key={card.tag} card={card} index={index} onLearnMore={handleLearnMore} />
          ))}
        </div>
      </div>
    </section>
  );
}
